import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { AppCrashFallback } from "./components/AppCrashFallback";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { queryClient } from "./lib/query-client";
import { setUnauthorizedHandler } from "./lib/api/client";
import { clearProtectedQueries } from "./lib/auth/protected-cache";
import {
  chunkReloadGuardSettled,
  installChunkReloadGuard,
} from "./lib/chunk-reload-guard";
import { registerServiceWorker } from "./lib/pwa/register";
import "./index.css";

// Before the first await: a chunk-load failure on a stale deploy must be
// caught even if it happens while this module is still evaluating.
installChunkReloadGuard();
setUnauthorizedHandler(() => {
  clearProtectedQueries(queryClient);
  if (
    window.location.pathname !== "/login" &&
    window.location.pathname !== "/no-access"
  ) {
    const returnTo = window.location.pathname + window.location.search;
    window.location.replace(`/login?return_to=${encodeURIComponent(returnTo)}`);
  }
});

const container = document.querySelector("#root");
if (!container) throw new Error("#root is missing from the document shell");

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary
      fallback={(error, reset) => (
        <AppCrashFallback error={error} reset={reset} />
      )}
    >
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

// The tree mounted, so this load was not the stale one. Release the one-shot
// reload so the NEXT deploy gets its own recovery attempt.
chunkReloadGuardSettled();

// Registered AFTER mount so a service-worker failure can never delay or block
// first paint. The app is fully functional uninstalled; the worker only adds
// offline support and the update handshake.
void registerServiceWorker();
